import dayjs from 'dayjs';
import type { BookingFor } from '../../../lib/api/modules/patient-portal';
import { ageFrom } from './careTheme';

/** Plain data and rules for the public booking steps (kept out of component files for fast refresh). */

export interface WhoDetails {
  bookingFor: BookingFor | '';
  name: string;
  gender: string;
  dateOfBirth: string;
  guardianConfirmed: boolean;
  /** The parent/guardian booking for a minor — recorded with the consent (DPDP s.9). */
  guardianName: string;
}

export const EMPTY_WHO: WhoDetails = { bookingFor: '', name: '', gender: '', dateOfBirth: '', guardianConfirmed: false, guardianName: '' };

/** Every clinic on ClinVea Care is in India; slots, cut-offs and "today" are in this zone, never the browser's. */
export const CLINIC_TIME_ZONE = 'Asia/Kolkata';

/** Today's date in the clinic's zone as YYYY-MM-DD. A browser in Chicago at 9 pm is already tomorrow in Hyderabad. */
export function clinicToday(): string {
  return new Intl.DateTimeFormat('en-CA', { timeZone: CLINIC_TIME_ZONE, year: 'numeric', month: '2-digit', day: '2-digit' }).format(new Date());
}

/** The clinic's wall-clock time now as HH:mm (24 h). */
export function clinicNowHHmm(): string {
  const parts = new Intl.DateTimeFormat('en-GB', { timeZone: CLINIC_TIME_ZONE, hour: '2-digit', minute: '2-digit', hour12: false }).format(new Date());
  return parts.replace(/^24/, '00');
}

/** A fresh key for one booking attempt; the same key is sent again on a retry so a slow network can't book twice. */
export function newIdempotencyKey(): string {
  const c = globalThis.crypto as Crypto | undefined;
  if (c?.randomUUID) return c.randomUUID();
  return Array.from({ length: 32 }, () => Math.floor(Math.random() * 16).toString(16)).join('');
}

/** "+91 98765 43210", "09876543210" and "9876543210" are the same number. */
export function normaliseIndianMobile(raw: string): string {
  let d = raw.replace(/\D/g, '');
  if (d.length === 12 && d.startsWith('91')) d = d.slice(2);
  else if (d.length === 11 && d.startsWith('0')) d = d.slice(1);
  return d;
}

/** Why the details can't be used yet, or null when they're complete and consistent. */
export function whoProblem(w: WhoDetails): 'incomplete' | 'selfMinor' | 'guardian' | null {
  const age = ageFrom(w.dateOfBirth);
  if (!w.bookingFor || w.name.trim().length < 2 || !w.gender || age == null || age < 0 || age > 120
      || w.dateOfBirth > dayjs().format('YYYY-MM-DD')) return 'incomplete';
  if (w.bookingFor === 'SELF' && age < 18) return 'selfMinor';
  if (w.bookingFor !== 'SELF' && age < 18 && (!w.guardianConfirmed || w.guardianName.trim().length < 2)) return 'guardian';
  return null;
}

export interface AccountChoice {
  wanted: boolean;
  email: string;
  password: string;
  termsAccepted: boolean;
}

export const NO_ACCOUNT: AccountChoice = { wanted: false, email: '', password: '', termsAccepted: false };

